import { find } from 'lodash-es';
import { inject, injectable } from 'tsyringe';
import { CachedResponseApi } from '../BaseApi';
import { ICache } from '../Cache';
import { BondTimeRange, TimeRange, TopPositionsType } from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import { Account, Apis, MiddlewareConfig, ModelRtbs, Quote, Sector } from '../types';
import { AccountWithExtraInfo, PositionAccount } from '../types/Account.type';
import {
    AccountPositionBreakdownStat,
    AccountSecurityTypeBreakdownStat,
    AccountSizesBreakdownStat,
    AccountTopPositionsBreakdownStat,
    AccountTypesBreakdownStat,
    MarketCapBreakdownStat,
    QuoteHistory,
    QuoteStat,
    SectorsBreakdownStat,
    TopPositionsStat,
} from '../types/Stat.type';
import { formatMarketCapBreakdownChartData, getEtfToCompare, parseOptionSymbol } from '../utils';
import {
    formatAccountPositionTypeBreakdownStat,
    formatAccountSecurityTypeBreakdownStat,
    formatAccountSizesBreakdownStat,
    formatAccountTypesBreakdownStat,
    formatBondStat,
    formatQuoteStat,
    formatSectorsBreakdownStat,
    formatTopPositionsBreakdownChartData,
    formatTopPositionsStat,
} from '../utils/statUtils';
import type { AccountApi } from './AccountApi';
import type { BranchApi } from './BranchApi';
import type { GroupApi } from './GroupApi';
import type { ModelApi } from './ModelApi';
import type { QuoteApi } from './QuoteApi';

@injectable()
export class StatApi extends CachedResponseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) cache: ICache,
        @inject(ApiInjectToken.BranchApi) private readonly branchApi: BranchApi,
        @inject(ApiInjectToken.ModelApi) private readonly modelApi: ModelApi,
        @inject(ApiInjectToken.AccountApi) private readonly accountApi: AccountApi,
        @inject(ApiInjectToken.GroupApi) private readonly groupApi: GroupApi,
        @inject(ApiInjectToken.QuoteApi) private readonly quoteApi: QuoteApi,
    ) {
        super(apis, config, cache);
    }

    async getBranchTopPositionsStat(by = TopPositionsType.Weight, maxPositions = 10): Promise<TopPositionsStat> {
        const portfolio = await this.branchApi.getPortfolio();
        return formatTopPositionsStat(portfolio.entries, by, maxPositions);
    }

    async getBranchMarketCapBreakdownStat(): Promise<MarketCapBreakdownStat> {
        const portfolio = await this.branchApi.getPortfolio();
        return formatMarketCapBreakdownChartData(portfolio.entries);
    }

    async getBranchSectorsBreakdownStat(): Promise<SectorsBreakdownStat> {
        const [sectors, groups] = await Promise.all([
            this.apis.orbis.advisory.analytics.overexposureAnalysis('Sector', 'branch', 'all', { threshold: 0 }),
            this.apis.orbis.advisory.analytics.overexposureAnalysis('IndustryGroup', 'branch', 'all', { threshold: 0 }),
        ]);

        return formatSectorsBreakdownStat(sectors.data, groups.data);
    }

    async getBranchAccountSizesBreakdownStat(): Promise<AccountSizesBreakdownStat> {
        const accounts = await this.branchApi.getAccounts();
        return formatAccountSizesBreakdownStat(accounts);
    }

    async getQuoteAccountSizesBreakdownStat(symbol: string): Promise<AccountSizesBreakdownStat> {
        const data = await Promise.all([
            this.branchApi.getAccounts(),
            this.apis.orbis.user.positionSearch({ symbols: symbol, loadQuotes: true }),
        ]);
        const allAccounts = data[0];
        const accountsHoldingPosition = data[1].data as PositionAccount[];
        const accountIds = accountsHoldingPosition.map((position) => position.accountId);
        const filteredAccounts = allAccounts.filter((account) => accountIds.includes(account.accountId));
        return formatAccountSizesBreakdownStat(filteredAccounts);
    }

    async getQuoteAccountTypesBreakdownStat(symbol: string): Promise<AccountTypesBreakdownStat> {
        const data = await Promise.all([
            this.branchApi.getAccounts(),
            this.apis.orbis.user.positionSearch({ symbols: symbol, loadQuotes: true }),
        ]);
        const allAccounts = data[0];
        const accountsHoldingPosition = data[1].data as PositionAccount[];
        const accountsHoldingPositionIds = accountsHoldingPosition.map((position) => position.accountId);
        return formatAccountTypesBreakdownStat(
            allAccounts.filter((account) => accountsHoldingPositionIds.includes(account.accountId)),
        );
    }

    async getBranchAccountTypesBreakdownStat(): Promise<AccountTypesBreakdownStat> {
        const { data } = await this.apis.orbis.advisory.userAccountManagement.accountsStats();
        return formatAccountTypesBreakdownStat(data);
    }

    async getModelTopPositionsStat(
        modelId: number,
        by = TopPositionsType.Weight,
        maxPositions = 10,
    ): Promise<TopPositionsStat> {
        const portfolio = await this.modelApi.getPortfolio(modelId);
        return formatTopPositionsStat(portfolio.entries, by, maxPositions);
    }

    async getModelMarketCapBreakdownStat(modelId: number): Promise<MarketCapBreakdownStat> {
        const portfolio = await this.modelApi.getPortfolio(modelId);
        return formatMarketCapBreakdownChartData(portfolio.entries);
    }

    async getModelSectorsBreakdownStat(modelId: number): Promise<SectorsBreakdownStat> {
        const [sectors, groups] = await Promise.all([
            this.apis.orbis.advisory.analytics.overexposureAnalysis('Sector', 'model', `${modelId}`, { threshold: 0 }),
            this.apis.orbis.advisory.analytics.overexposureAnalysis('IndustryGroup', 'model', `${modelId}`, {
                threshold: 0,
            }),
        ]);

        return formatSectorsBreakdownStat(sectors.data, groups.data);
    }

    async getModelAccountSizesBreakdownStat(modelId: number): Promise<AccountSizesBreakdownStat> {
        const data = await Promise.all([
            this.modelApi.getModels(),
            this.apis.orbis.advisory.models.memberRealtimeBalances(`${modelId}`),
        ]);

        const accounts: Account[] = data[0].find((model) => model.id === modelId)?.accounts || [];
        const rtbs = data[1].data as ModelRtbs[];

        const accountsWithExtraInfo: AccountWithExtraInfo[] = accounts.map((account) => {
            const {
                balance = 0,
                cashOnAccount = 0,
                markToMarket = 0,
                sodCashBalance = 0,
                sodMarginBalance = 0,
                sodShortBalance = 0,
                totalDividendAmount = 0,
                totalDividendYield = 0,
            } = find(rtbs, (item) => item.accountId === account.accountId) || {};

            return {
                ...account,
                balance,
                cashOnAccount,
                markToMarket,
                sodCashBalance,
                sodMarginBalance,
                sodShortBalance,
                totalDividendAmount,
                totalDividendYield,
            };
        });

        return formatAccountSizesBreakdownStat(accountsWithExtraInfo);
    }

    async getModelAccountTypesBreakdownStat(modelId: number): Promise<AccountTypesBreakdownStat> {
        const { data } = await this.apis.orbis.advisory.models.memberAccountStatistics(`${modelId}`);
        return formatAccountTypesBreakdownStat(data);
    }

    async getAccountTopPositionsStat(
        accountId: number,
        by = TopPositionsType.Weight,
        maxPositions = 10,
    ): Promise<TopPositionsStat> {
        const portfolio = await this.accountApi.getPortfolio({ accountId });
        return formatTopPositionsStat(portfolio.entries, by, maxPositions);
    }

    async getAccountTopPositionsBreakdownStat(
        accountId: number,
        topPositions?: number,
    ): Promise<AccountTopPositionsBreakdownStat> {
        const portfolio = await this.accountApi.getPortfolio({ accountId });
        return formatTopPositionsBreakdownChartData(portfolio.entries, topPositions);
    }

    async getAccountMarketCapBreakdownStat(accountId: number): Promise<MarketCapBreakdownStat> {
        const portfolio = await this.accountApi.getPortfolio({ accountId });
        return formatMarketCapBreakdownChartData(portfolio.entries);
    }

    async getAccountSectorsBreakdownStat(accountId: number): Promise<SectorsBreakdownStat> {
        const [sectors, groups] = await Promise.all([
            this.apis.orbis.advisory.analytics.overexposureAnalysis('Sector', 'account', `${accountId}`, {
                threshold: 0,
            }),
            this.apis.orbis.advisory.analytics.overexposureAnalysis('IndustryGroup', 'account', `${accountId}`, {
                threshold: 0,
            }),
        ]);

        const etfSymbols: string[] = (
            await Promise.all(
                (groups.data as { quote: Quote }[]).map((group) =>
                    this.apis.orbis.research.companyResearch(
                        (group.quote.option ? group.quote.underlying?.symbol : group.quote.symbol) || '',
                    ),
                ),
            )
        )
            .filter((item) => item.data.etf)
            .map((item) => item.data.etfSymbolEntry.symbol);

        const etfSectors = await Promise.all(
            etfSymbols
                .map((symbol) => this.apis.orbis.research.etfs.details({ symbol: symbol }))
                .map((item) => item.then((data) => data.data.Sectors ?? [])),
        );

        return formatSectorsBreakdownStat(sectors.data, groups.data, etfSectors as Sector[][]);
    }

    async getGroupTopPositionsStat(
        groupId: number,
        by = TopPositionsType.Weight,
        maxPositions = 10,
    ): Promise<TopPositionsStat> {
        const portfolio = await this.groupApi.getPortfolio(groupId);
        return formatTopPositionsStat(portfolio.entries, by, maxPositions);
    }

    async getGroupMarketCapBreakdownStat(groupId: number): Promise<MarketCapBreakdownStat> {
        const portfolio = await this.groupApi.getPortfolio(groupId);
        return formatMarketCapBreakdownChartData(portfolio.entries);
    }

    async getGroupAccountSizesBreakdownStat(groupId: number): Promise<AccountSizesBreakdownStat> {
        const group = await this.groupApi.getGroup(groupId);
        return formatAccountSizesBreakdownStat(group?.accounts || []);
    }

    async getGroupSectorsBreakdownStat(groupId: number): Promise<SectorsBreakdownStat> {
        const [sectors, groups] = await Promise.all([
            this.apis.orbis.advisory.analytics.overexposureAnalysis('Sector', 'group', `${groupId}`, { threshold: 0 }),
            this.apis.orbis.advisory.analytics.overexposureAnalysis('IndustryGroup', 'group', `${groupId}`, {
                threshold: 0,
            }),
        ]);

        return formatSectorsBreakdownStat(sectors.data, groups.data);
    }

    async getGroupAccountTypesBreakdownStat(groupId: number): Promise<AccountTypesBreakdownStat> {
        const { data } = await this.apis.orbis.advisory.groups.groupAccountsStats(`${groupId}`);
        return formatAccountTypesBreakdownStat(data);
    }

    async getAccountPositionTypeBreakdownStat(accountId: number): Promise<AccountPositionBreakdownStat> {
        const portfolio = await this.accountApi.getPortfolio({ accountId });
        return formatAccountPositionTypeBreakdownStat(portfolio.entries);
    }

    async getAccountSecurityTypeBreakdownStat(accountId: number): Promise<AccountSecurityTypeBreakdownStat> {
        const portfolio = await this.accountApi.getPortfolio({ accountId });

        return formatAccountSecurityTypeBreakdownStat(portfolio.entries);
    }

    async getUserAccountTypesBreakdownStat(userId: number): Promise<AccountTypesBreakdownStat> {
        const { data } = await this.apis.orbis.advisory.userAccountManagement.accounts({ uid: userId, loadRtb: true });
        const accounts: Account[] = data.map((item: { account: Account }) => item.account);
        return formatAccountTypesBreakdownStat(accounts);
    }

    async getQuoteStat(symbol: string, range: TimeRange, crypto?: boolean): Promise<QuoteStat> {
        const etfSymbol = getEtfToCompare(symbol);
        const [data, etfData] = await Promise.all(
            crypto && range === TimeRange.OneDay
                ? // FIX ME: Change to /crypto when back end fixes
                  [this.getQuoteIntraday(symbol)]
                : crypto
                  ? [this.getCryptoHistorical(symbol, range)]
                  : range === TimeRange.OneDay
                    ? [this.getQuoteIntraday(symbol), this.getQuoteIntraday(etfSymbol)]
                    : [this.getQuoteHistorical(symbol, range), this.getQuoteHistorical(etfSymbol, range)],
        );

        const [currentQuote, currentQuoteEtf] = await Promise.all([
            this.quoteApi.getQuote(symbol, crypto),
            this.quoteApi.getQuote(etfSymbol, crypto),
        ]);

        const combinedData = currentQuote
            ? [...data, { price: currentQuote.lastPrice, date: currentQuote.updateTime }]
            : data;
        const combinedDataEtf = currentQuoteEtf
            ? [...(etfData || []), { price: currentQuoteEtf.lastPrice, date: currentQuoteEtf.updateTime }]
            : etfData || [];

        return formatQuoteStat(combinedData, crypto ? [] : combinedDataEtf);
    }

    async getQuoteIntraday(symbol: string): Promise<QuoteHistory[]> {
        let data;
        if (this.apis.aesMidas) {
            data = (await this.apis.aesMidas.quotesReformat.getEquityIntraday({ symbol })).data;
        } else {
            data = (await this.apis.orbis.quotes.intradayChartData({ symbol })).data;
        }
        return data as QuoteHistory[];
    }

    async getCryptoHistorical(symbol: string, range?: TimeRange): Promise<QuoteHistory[]> {
        // FIXME: missing from docs. Waiting for backend update
        const response = await this.apis.orbis.getApiClient().get<QuoteHistory[]>('quotes/crypto/historical', {
            params: { symbol, range },
        });
        return response.data;
    }

    async getOptionHistorical(symbol: string, range?: TimeRange): Promise<QuoteHistory[]> {
        let data;
        if (this.apis.aesMidas) {
            data = (await this.apis.aesMidas.quotesReformat.getOptionHistorical({ symbol, range })).data;
        } else {
            // FIXME: missing from docs. Waiting for backend update
            data = (
                await this.apis.orbis.getApiClient().get<QuoteHistory[]>('quotes/option/historical', {
                    params: { symbol, range },
                })
            ).data;
        }
        return data;
    }

    async getQuoteHistorical(symbol: string, range?: TimeRange) {
        let data;
        if (this.apis.aesMidas) {
            data = (await this.apis.aesMidas.quotesReformat.getEquityHistorical({ symbol, range })).data;
        } else {
            data = (await this.apis.orbis.quotes.historical({ symbol, range: range ?? TimeRange.SevenMonth })).data;
        }
        return data;
    }

    // Start and end date format is YYYY-MM-DD
    async getBondHistorical(isin: string, frequency: BondTimeRange, start: string, end: string): Promise<QuoteStat> {
        const response = await this.apis.insight.fixedIncome.fixedIncomeHistorical({
            instrument_id: isin,
            frequency,
            start,
            end,
        });
        const data = formatBondStat(response.data.data);
        return data;
    }

    async getQuoteOptionStat(symbol: string, range: TimeRange): Promise<QuoteStat> {
        const underlyingSymbol = parseOptionSymbol(symbol)?.symbol || '';
        const [data, underlyingSymbolData] = await Promise.all([
            this.getOptionHistorical(symbol, range),
            this.getQuoteHistorical(underlyingSymbol, range),
        ]);

        const [currentOptionQuote, currentQuote] = await Promise.all([
            this.quoteApi.getOptionQuotes([symbol]).then((quotes) => quotes.find((quote) => quote.symbol === symbol)),
            this.quoteApi.getQuote(underlyingSymbol),
        ]);

        const combinedData = currentOptionQuote
            ? [...data, { price: currentOptionQuote.lastPrice, date: currentOptionQuote.updateTime }]
            : data;
        const combinedComparisonData = currentQuote
            ? [...underlyingSymbolData, { price: currentQuote.lastPrice, date: currentQuote.updateTime }]
            : underlyingSymbolData;
        return formatQuoteStat(combinedData, combinedComparisonData);
    }
}
